import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import {
  Users,
  Trophy,
  BookOpen,
  TrendingUp,
  Plus,
  Target,
  Award,
  ChevronDown,
} from "lucide-react";
import { getMe, getStudentsBySchool, getQuizResults, listModules, getModule, getTeacherClasses } from "../api/client";
import { useTranslation } from "../hooks/useTranslation";


export default function TeacherDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [allSchoolStudents, setAllSchoolStudents] = useState([]);
  const [students, setStudents] = useState([]);
  const [allQuizResults, setAllQuizResults] = useState([]);
  const [quizNamesMap, setQuizNamesMap] = useState({});
  const [error, setError] = useState(null);

  // Klassen-Dropdown State
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [classLoading, setClassLoading] = useState(false);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Initial Load: User, Klassen, alle Schüler der Schule
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const me = await getMe();
        if (!me || !me.id) {
          setUser(null);
          setLoading(false);
          return;
        }
        setUser(me);

        // Klassen der Schule laden
        const classList = await getTeacherClasses();
        setClasses(Array.isArray(classList) ? classList : []);

        // Standard: eigene Klasse des Lehrers vorauswählen
        const defaultClass = me.school_class || (classList[0]?.name ?? "");
        setSelectedClass(defaultClass);

        // Alle Schüler der Schule laden (einmalig, Filterung passiert im Client)
        const allStudents = await getStudentsBySchool(me.school_name || "");
        setAllSchoolStudents(Array.isArray(allStudents) ? allStudents : []);

        // Module/Lektionsnamen laden
        try {
          const allModules = await listModules();
          const namesMap = {};
          if (Array.isArray(allModules)) {
            for (const mod of allModules) {
              try {
                const moduleData = await getModule(mod.id);
                if (moduleData && Array.isArray(moduleData.lessons)) {
                  moduleData.lessons.forEach((lesson) => {
                    const moduleName = moduleData.title || `Modul ${mod.id}`;
                    const lessonName = lesson.title || `Lektion ${lesson.id}`;
                    namesMap[lesson.id] = `${moduleName} - ${lessonName}`;
                  });
                }
              } catch (err) {
                console.error(`Error loading module ${mod.id}:`, err);
              }
            }
          }
          setQuizNamesMap(namesMap);
        } catch (e) {
          console.error("Error loading lesson names:", e);
          setQuizNamesMap({});
        }
      } catch (e) {
        console.error("TeacherDashboard load error:", e);
        setError("Fehler beim Laden der Daten");
        setAllSchoolStudents([]);
        setAllQuizResults([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Wenn sich selectedClass oder allSchoolStudents ändert: Schüler + Quiz-Ergebnisse filtern & laden
  useEffect(() => {
    if (!selectedClass || allSchoolStudents.length === 0) {
      setStudents([]);
      setAllQuizResults([]);
      return;
    }

    (async () => {
      setClassLoading(true);
      try {
        const classStudents = allSchoolStudents.filter(
          (s) => s.school_class === selectedClass
        );
        setStudents(classStudents);

        const studentIds = classStudents.map((s) => s.id);
        if (studentIds.length > 0) {
          const resultsPromises = studentIds.map((studentId) =>
            getQuizResults(studentId).catch(() => [])
          );
          const resultsArrays = await Promise.all(resultsPromises);
          const flatResults = resultsArrays.flat().filter((r) => r != null);
          setAllQuizResults(flatResults);
        } else {
          setAllQuizResults([]);
        }
      } catch (e) {
        console.error("Error loading class data:", e);
        setAllQuizResults([]);
      } finally {
        setClassLoading(false);
      }
    })();
  }, [selectedClass, allSchoolStudents]);

  // ========== KPI BERECHNUNGEN ==========

  const totalQuizzesCompleted = allQuizResults.length;

  const calculateAveragePercentage = () => {
    if (students.length === 0) return 0;
    const studentAverages = students.map((student) => {
      const studentQuizzes = allQuizResults.filter((r) => r.user_id === student.id);
      if (studentQuizzes.length === 0) return 0;
      return (
        studentQuizzes.reduce((sum, quiz) => sum + (quiz.percentage || 0), 0) /
        studentQuizzes.length
      );
    });
    const totalAverage =
      studentAverages.reduce((sum, avg) => sum + avg, 0) / studentAverages.length;
    return Math.round(totalAverage);
  };

  const totalPointsEarned = allQuizResults.reduce(
    (sum, result) => sum + (result.score || 0),
    0
  );

  // ========== MODAL FUNKTIONEN ==========

  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
  };

  const getStudentQuizResults = (studentId) => {
    return allQuizResults
      .filter((r) => r.user_id === studentId)
      .sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
  };

  const getStudentTotalPoints = (studentId) => {
    return allQuizResults
      .filter((r) => r.user_id === studentId)
      .reduce((sum, quiz) => sum + (quiz.score || 0), 0);
  };

  // ========== LOADING & ERROR STATES ==========

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">{t.teacher.loadingDashboard}</p>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="py-5 text-center">
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-5">
            <h5 className="text-danger">{t.teacher.noUserFound}</h5>
            <p className="text-muted">{t.teacher.pleaseLogin}</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5 text-center">
        <Card className="border-danger">
          <Card.Body className="py-5">
            <h5 className="text-danger">{error}</h5>
            <Button
              variant="outline-primary"
              onClick={() => window.location.reload()}
              className="mt-3"
            >
              {t.teacher.reload}
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <main
      className="dashboard-main"
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <Container
        className="py-4"
        style={{
          maxWidth: "none",
          paddingLeft: "2rem",
          paddingRight: "2rem",
        }}
      >
        {/* Header */}
        <div className="mb-4">
          <h1 className="mb-2">{t.teacher.dashboard}</h1>
          <p className="text-muted mb-1">
            {t.common.welcomeBack} <strong>{user.full_name || user.fullname}</strong>!
          </p>
          <p className="text-muted mb-0">
            <strong>{t.teacher.school}</strong> {user.school_name || "Keine Schule"}
          </p>
        </div>

        {/* Klassen-Auswahl */}
        <Row className="g-3 mb-4">
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="d-flex flex-wrap align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                  <Users size={20} className="text-primary" />
                  <strong style={{ whiteSpace: "nowrap" }}>
                    {t.teacher.class || "Klasse"}:
                  </strong>
                </div>
                <div style={{ minWidth: "220px", maxWidth: "320px", flex: "1" }}>
                  <Form.Select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    disabled={classLoading || classes.length === 0}
                    style={{ borderRadius: "8px", fontWeight: "500" }}
                  >
                    {classes.length === 0 ? (
                      <option value="">Keine Klassen verfügbar</option>
                    ) : (
                      classes.map((cls) => (
                        <option key={cls.id} value={cls.name}>
                          {cls.name}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </div>
                {classLoading && (
                  <Spinner animation="border" size="sm" variant="primary" />
                )}
                <span className="text-muted small ms-auto">
                  {students.length} {students.length === 1 ? "Schüler" : "Schüler"} in dieser Klasse
                </span>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* KPI Cards */}
        <Row className="g-3 mb-4">
          <Col lg={4} md={6} sm={6} xs={12}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="d-flex align-items-center">
                <div className="rounded-circle p-3 me-3" style={{ backgroundColor: "#e3f2fd" }}>
                  <Trophy size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="mb-0 fw-bold">{totalQuizzesCompleted}</h3>
                  <p className="text-muted mb-0 small">{t.teacher.quizzesCompleted}</p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} md={6} sm={6} xs={12}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="d-flex align-items-center">
                <div className="rounded-circle p-3 me-3" style={{ backgroundColor: "#f3e5f5" }}>
                  <TrendingUp size={24} style={{ color: "#9c27b0" }} />
                </div>
                <div>
                  <h3 className="mb-0 fw-bold">{calculateAveragePercentage()}%</h3>
                  <p className="text-muted mb-0 small">{t.teacher.averagePerformance}</p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} md={6} sm={6} xs={12}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="d-flex align-items-center">
                <div className="rounded-circle p-3 me-3" style={{ backgroundColor: "#fff3e0" }}>
                  <Award size={24} className="text-warning" />
                </div>
                <div>
                  <h3 className="mb-0 fw-bold">{totalPointsEarned}</h3>
                  <p className="text-muted mb-0 small">{t.teacher.pointsCollected}</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Content-Verwaltung Button */}
        <Row className="g-3 mb-4">
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1">{t.contentCreator.title}</h5>
                  <p className="text-muted mb-0 small">
                    {t.contentCreator.subtitle}
                  </p>
                </div>
                <Button
                  variant="primary"
                  onClick={() => navigate("/teacher/content")}
                  className="d-flex align-items-center gap-2"
                >
                  <Plus size={20} />
                  {t.contentCreator.manageButton}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Schüler-Liste */}
        <Row className="g-3 mb-4">
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <Users size={20} className="me-2 text-primary" />
                  <h5 className="mb-0">
                    {t.teacher.studentsInClass} – <span className="text-primary">{selectedClass}</span> ({students.length})
                  </h5>
                </div>
              </Card.Header>
              <Card.Body>
                {classLoading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted small">Klasse wird geladen…</p>
                  </div>
                ) : students.length === 0 ? (
                  <div className="text-center py-5">
                    <Users size={48} className="text-muted mb-3" />
                    <h6 className="text-muted mb-2">{t.teacher.noStudentsFound}</h6>
                    <p className="text-muted small mb-0">
                      {selectedClass
                        ? `In der Klasse "${selectedClass}" sind noch keine Schüler registriert.`
                        : "Bitte wählen Sie eine Klasse aus."}
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th style={{ paddingLeft: "48px" }}>Name</th>
                          <th className="text-center">{t.teacher.points}</th>
                          <th className="text-center">{t.teacher.level}</th>
                          <th className="text-center">{t.teacher.average}</th>
                          <th className="text-center">{t.teacher.quizzesDone}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students
                          .map((student) => {
                            const studentQuizzes = allQuizResults.filter(
                              (r) => r.user_id === student.id
                            );
                            const studentAverage =
                              studentQuizzes.length > 0
                                ? studentQuizzes.reduce(
                                    (sum, quiz) => sum + (quiz.percentage || 0),
                                    0
                                  ) / studentQuizzes.length
                                : 0;
                            const totalPoints = getStudentTotalPoints(student.id);
                            return {
                              ...student,
                              calculatedAverage: studentAverage,
                              totalPoints,
                            };
                          })
                          .sort((a, b) => b.calculatedAverage - a.calculatedAverage)
                          .map((student, index) => {
                            const studentQuizCount = allQuizResults.filter(
                              (r) => r.user_id === student.id
                            ).length;
                            const studentAverage = Math.round(student.calculatedAverage);

                            return (
                              <tr
                                key={student.id}
                                onClick={() => handleStudentClick(student)}
                                style={{ cursor: "pointer" }}
                              >
                                <td>
                                  <Badge bg={index < 3 ? "warning" : "light"} text="dark">
                                    {index + 1}
                                  </Badge>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="rounded-circle me-2"
                                      style={{
                                        width: 32,
                                        height: 32,
                                        backgroundColor: `#${
                                          student.avatar_color ||
                                          student.avatarcolor ||
                                          "1E88E5"
                                        }`,
                                      }}
                                    />
                                    <strong>{student.full_name || student.fullname}</strong>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <Badge bg="primary">{student.totalPoints}</Badge>
                                </td>
                                <td className="text-center">
                                  <Badge bg="info">{student.level || 1}</Badge>
                                </td>
                                <td className="text-center">
                                  <Badge
                                    bg={
                                      studentAverage >= 80
                                        ? "success"
                                        : studentAverage >= 50
                                        ? "warning"
                                        : studentAverage > 0
                                        ? "danger"
                                        : "secondary"
                                    }
                                  >
                                    {studentAverage > 0 ? `${studentAverage}%` : "-"}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <span className="text-muted">{studentQuizCount}</span>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

      </Container>

      {/* Student Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center">
              {selectedStudent && (
                <>
                  <div
                    className="rounded-circle me-3"
                    style={{
                      width: 40,
                      height: 40,
                      backgroundColor: `#${
                        selectedStudent.avatar_color ||
                        selectedStudent.avatarcolor ||
                        "1E88E5"
                      }`,
                    }}
                  />
                  <div>
                    <h5 className="mb-0">
                      {selectedStudent.full_name || selectedStudent.fullname}
                    </h5>
                    <small className="text-muted">
                      {t.teacher.quizResults || "Quiz-Ergebnisse"}
                    </small>
                  </div>
                </>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStudent && (
            <>
              <Row className="g-3 mb-4">
                <Col md={4}>
                  <Card className="border-0 bg-light">
                    <Card.Body className="text-center">
                      <Trophy size={24} className="text-primary mb-2" />
                      <h4 className="mb-0">{selectedStudent.totalPoints}</h4>
                      <small className="text-muted">{t.teacher.points}</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 bg-light">
                    <Card.Body className="text-center">
                      <Target size={24} className="text-success mb-2" />
                      <h4 className="mb-0">
                        {Math.round(selectedStudent.calculatedAverage)}%
                      </h4>
                      <small className="text-muted">{t.teacher.average}</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 bg-light">
                    <Card.Body className="text-center">
                      <BookOpen size={24} className="text-info mb-2" />
                      <h4 className="mb-0">
                        {getStudentQuizResults(selectedStudent.id).length}
                      </h4>
                      <small className="text-muted">{t.teacher.quizzesDone}</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <h6 className="mb-3">
                {t.teacher.allQuizResults || "Alle Quiz-Ergebnisse"}
              </h6>
              {getStudentQuizResults(selectedStudent.id).length === 0 ? (
                <div className="text-center py-4">
                  <BookOpen size={48} className="text-muted mb-3" />
                  <p className="text-muted">
                    {t.teacher.noQuizCompleted || "Noch keine Quiz absolviert"}
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>{t.teacher.dateTime || "Datum & Uhrzeit"}</th>
                        <th>{t.teacher.quiz || "Quiz"}</th>
                        <th className="text-center">
                          {t.teacher.result || "Ergebnis"}
                        </th>
                        <th className="text-center">{t.teacher.points}</th>
                        <th className="text-center">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getStudentQuizResults(selectedStudent.id).map((quiz, idx) => (
                        <tr key={idx}>
                          <td>
                            <small className="text-muted">
                              {new Date(quiz.created_date).toLocaleString("de-DE", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </small>
                          </td>
                          <td>
                            <small>
                              {quizNamesMap[quiz.quiz_id] ||
                                quiz.quiz_name ||
                                quiz.quiz_title ||
                                `Quiz #${quiz.quiz_id}`}
                            </small>
                          </td>
                          <td className="text-center">
                            <small>
                              {quiz.correct_answers}/{quiz.total_questions}
                            </small>
                          </td>
                          <td className="text-center">
                            <Badge bg="primary">{quiz.score}</Badge>
                          </td>
                          <td className="text-center">
                            <Badge
                              bg={
                                quiz.percentage >= 80
                                  ? "success"
                                  : quiz.percentage >= 50
                                  ? "warning"
                                  : "danger"
                              }
                            >
                              {quiz.percentage}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            {t.common.close || "Schließen"}
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}
